import logging from '../logging';
import { AttrMap, IndexedMesh } from './indexed-mesh';

const log = logging('imw');

export abstract class IndexedMeshWriter<A extends AttrMap> {

    abstract computeAttributeCounts(): IndexedMesh<A>;

    abstract computeMeshData(mesh: IndexedMesh<A>): void;

    /**
     * Converts an IndexedMeshWriter into a concrete IndexedMesh. Most of the mesh
     * generation is handled by the writer, but this function totals the computed
     * attributes provided by the the mesh writer and allocates memory in the form
     * of an ArrayBuffer and the TypedArrays for each attribute that are backed
     * by the single contiguous buffer.
     */
    make(): IndexedMesh<A> {
        const
            mesh = this.computeAttributeCounts(),
            idxSize = mesh.index.size * mesh.index.count,
            idxType = (idxSize < 65536 ? Uint16Array : Uint32Array),
            idxByteSize = idxSize * idxType.BYTES_PER_ELEMENT,
            // we need the padded size for the index b/c the Float32 arrays will
            // require that we're aligned to 32-bit integers, i.e. 4 bytes
            idxPaddedSize = Math.ceil(idxByteSize / 4) * 4,
            posSize = mesh.position.size * mesh.position.count,
            posByteSize = posSize * Float32Array.BYTES_PER_ELEMENT,
            attrSize = Object.values(mesh.attrs).reduce(
                (total, a) => total + a.size * a.count, 0),
            byteSize =
                idxPaddedSize
                + posByteSize
                + attrSize * Float32Array.BYTES_PER_ELEMENT,
            data = new ArrayBuffer(byteSize);


        let start = 0;

        mesh.index.data = new idxType(data, start, idxSize);
        start += idxPaddedSize;

        mesh.position.data = new Float32Array(data, start, posSize);
        start += posByteSize;

        for(const name of Object.keys(mesh.attrs)) {
            const
                attr = mesh.attrs[name],
                size = attr.size * attr.count;

            attr.data = new Float32Array(data, start, size);

            start += size * Float32Array.BYTES_PER_ELEMENT;
        }

        this.computeMeshData(mesh);

        return mesh;
    }
}


